/* GFI Nexus — PDF merge helper
   ─────────────────────────────────────────────────────────────────
   Used by the Organic Audit reports that need to stitch together
   one cover document (generated by htmlToPdfBlob) with N annex
   PDFs already on disk / in storage. Built on pdf-lib because
   it's pure JS and runs in the browser — no Chromium, no server.

   Input is a list of items, each either a Blob/ArrayBuffer of a
   PDF, or { name, blob } for nicer error messages. Returns a
   single Blob of MIME type application/pdf. */

import { PDFDocument, PDFName } from "pdf-lib";

/* Cheap heuristic: a page with no Contents entry, or one whose
   combined content streams total under ~80 bytes, is almost
   certainly blank (no text, no images, no path operations). Used
   by mergePdfBlobs's `skipBlankPages` option to filter out the
   trailing whitespace pages that user-uploaded annex PDFs often
   carry — those were padding the Organic Audit cert pack out to
   dozens of useless sheets. We deliberately err on the side of
   keeping a page: anything with even a few drawing operators is
   considered non-blank. */
function isLikelyBlankPage(page) {
  try {
    const node = page.node;
    if (!node || typeof node.lookup !== "function") return false;
    const contents = node.lookup(PDFName.of("Contents"));
    if (!contents) return true;
    let totalBytes = 0;
    const collect = function (stream) {
      try {
        if (stream && typeof stream.getContents === "function") {
          const bytes = stream.getContents();
          if (bytes && typeof bytes.length === "number") totalBytes += bytes.length;
        }
      } catch (e) { /* unreadable stream — treat as non-blank to be safe */ totalBytes += 9999; }
    };
    if (Array.isArray(contents)) {
      contents.forEach(collect);
    } else if (typeof contents.asArray === "function") {
      contents.asArray().forEach(collect);
    } else {
      collect(contents);
    }
    /* < 80 bytes of content stream means at most a few PDF
       operators — not enough room for any visible glyph or shape.
       Pages that just set the color state and call it a day pass
       through here. */
    return totalBytes < 80;
  } catch (e) {
    return false;
  }
}

async function toBytes(input) {
  /* Accept Blob, ArrayBuffer, or Uint8Array — normalise to Uint8Array. */
  if (!input) return null;
  if (input instanceof Uint8Array) return input;
  if (input instanceof ArrayBuffer) return new Uint8Array(input);
  if (typeof input.arrayBuffer === "function") {
    const buf = await input.arrayBuffer();
    return new Uint8Array(buf);
  }
  return null;
}

/* Merge an ordered list of PDF inputs into a single PDF Blob.

   `items` is an array of either:
     - Blob / ArrayBuffer / Uint8Array of a PDF, or
     - { name, blob | bytes } — the name surfaces in console
       warnings when a single item fails to parse so the caller
       can pinpoint which annex is broken.

   `opts.skipBlankPages` — when truthy, drops pages whose content
   stream is < 80 bytes (heuristic for "essentially blank"). This
   strips out the whitespace pages that user-uploaded multi-page
   PDFs often carry without scrubbing real content. Off by default
   so existing callers don't suddenly lose pages they expected to
   see.

   Failures on individual items are logged and skipped (the rest
   of the merge proceeds). If EVERY item fails, throws. */
export async function mergePdfBlobs(items, opts) {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) throw new Error("mergePdfBlobs: no inputs");
  const skipBlanks = !!(opts && opts.skipBlankPages);
  const out = await PDFDocument.create();
  let merged = 0;
  let blanksSkipped = 0;
  for (const it of list) {
    const name = (it && it.name) || "pdf";
    const src = it && (it.blob || it.bytes) ? (it.blob || it.bytes) : it;
    try {
      const bytes = await toBytes(src);
      if (!bytes) { console.warn("[pdfMerge] skipping " + name + " — not a blob/bytes"); continue; }
      const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const allIndices = doc.getPageIndices();
      let keepIndices = allIndices;
      if (skipBlanks) {
        const allPages = doc.getPages();
        keepIndices = [];
        for (let i = 0; i < allPages.length; i++) {
          if (isLikelyBlankPage(allPages[i])) { blanksSkipped++; continue; }
          keepIndices.push(i);
        }
        /* Edge case: every page looked blank. Keep the first one so
           the annex isn't reduced to nothing — better to leak a
           false-positive blank than to lose the doc entirely. */
        if (keepIndices.length === 0 && allIndices.length > 0) keepIndices = [allIndices[0]];
      }
      const pages = await out.copyPages(doc, keepIndices);
      pages.forEach(function (p) { out.addPage(p); });
      merged++;
    } catch (e) {
      console.warn("[pdfMerge] failed to merge " + name + ":", e && e.message);
    }
  }
  if (merged === 0) throw new Error("mergePdfBlobs: every input failed to parse");
  if (skipBlanks && blanksSkipped > 0) {
    console.log("[pdfMerge] skipped " + blanksSkipped + " likely-blank page(s)");
  }
  const saved = await out.save();
  return new Blob([saved], { type: "application/pdf" });
}
